import type { SseEvent } from '@/shared/lib/sse/parser';

/* Pure decision for the global "Чат" nav signal, extracted from useChatSignal
   so the branchy semantics are unit-testable without a DOM or a live stream.

   A dispatcher should be signalled on exactly three kinds of incoming activity:
     1. a real operator message (author_type === 'OPERATOR'),
     2. a real consumer (B2C client) message (author_type === 'CONSUMER'), and
     3. a brand-new geo-approval request the dispatcher must act on
        (kind === 'APPROVAL_REQUEST' && payload.status === 'PENDING').

   The dispatcher's own approve/reject republishes a SYSTEM message with a
   resolved status (APPROVED/REJECTED) — that must NOT signal, or every decision
   would re-light the badge. Malformed / non-chat events never signal. */

export interface ChatSignal {
  /** Whether this event should bump the badge / fire a cue. */
  signal: boolean;
  /** True when it's a new approval request (vs a plain operator message). */
  isApproval: boolean;
}

const NO_SIGNAL: ChatSignal = { signal: false, isApproval: false };

export function shouldSignalChat(ev: SseEvent): ChatSignal {
  if (ev.event !== 'chat_message') return NO_SIGNAL;

  let authorType: string | undefined;
  let kind: string | undefined;
  let status: string | undefined;
  try {
    const parsed = JSON.parse(ev.data) as {
      author_type?: unknown;
      kind?: unknown;
      payload?: unknown;
    };
    if (typeof parsed.author_type === 'string') authorType = parsed.author_type;
    if (typeof parsed.kind === 'string') kind = parsed.kind;
    const payload = parsed.payload;
    if (payload && typeof payload === 'object' && 'status' in payload) {
      const s = (payload as { status?: unknown }).status;
      if (typeof s === 'string') status = s;
    }
  } catch {
    return NO_SIGNAL;
  }

  const isOperatorMessage = authorType === 'OPERATOR';
  const isConsumerMessage = authorType === 'CONSUMER';
  const isNewApproval = kind === 'APPROVAL_REQUEST' && status === 'PENDING';
  if (!isOperatorMessage && !isConsumerMessage && !isNewApproval) return NO_SIGNAL;
  return { signal: true, isApproval: isNewApproval };
}
